import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Eye, X } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const CERT_BASE = "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts";

const CREDENTIALS = [
  {
    n: "01",
    title: "Grado en Psicología",
    institution: "Universitat de Barcelona",
    year: "2018",
    description:
      "Aquí aprendí lo esencial: por qué las personas rinden, se comprometen o se van no es cuestión de suerte, tiene explicación. Estudiar el comportamiento humano dentro de las organizaciones me dio la base científica sobre la que hoy construyo cada diagnóstico que hago para mis clientes.",
    image: `${CERT_BASE}/zl5bshsr_PSICOLOGIA.webp`,
    // % of image (top, left, width, height) — sensitive data covered by blur
    redactions: [
      { t: 38, l: 24, w: 54, h: 6 },  // Francesc Riera Romea
      { t: 45, l: 13, w: 74, h: 8 },  // fecha nacimiento + lugar
      { t: 86, l: 12, w: 78, h: 7 },  // 3 firmas + nombres
      { t: 93, l: 1,  w: 16, h: 5 },  // 004A-090472
    ],
  },
  {
    n: "02",
    title: "Curso superior de Gestión del Talento y Competencias Digitales",
    institution: "Universitat Internacional de Valencia (VIU) · Deusto Formación",
    year: "2026",
    description:
      "350 horas que me sirvieron para poner orden y método a lo que ya intuía: cómo seleccionar, integrar, evaluar y desarrollar talento de forma estructurada. Esta formación me dio el marco que necesitaba para diseñar herramientas como el Talent Health Check con rigor, no a base de prueba y error.",
    image: `${CERT_BASE}/65jgxpnk_GESTI%C3%93N%20DEL%20TALENTO.webp`,
    redactions: [
      { t: 26, l: 15, w: 70, h: 7 },  // FRANCESC RIERA ROMEA
      { t: 33, l: 15, w: 70, h: 5 },  // nº expediente 9555863
      { t: 88, l: 66, w: 28, h: 9 },  // firma director
    ],
  },
  {
    n: "03",
    title: "Curso de Coaching y preparación mental",
    institution: "QuantumBCN",
    year: "2021",
    description:
      "Acompañar a un founder no es solo analizar datos, es sostener conversaciones difíciles en momentos de mucha presión. Aquí aprendí a hacerlo bien: cómo ayudar a un líder a pensar con claridad y tomar decisiones cuando todo aprieta. Es lo que hace que mi trabajo sea acompañamiento real, no solo un informe.",
    image: `${CERT_BASE}/h9np9og6_COACHING.webp`,
    redactions: [
      { t: 31, l: 10, w: 60, h: 7 },  // certifica que Francesc Riera Romea
      { t: 72, l: 13, w: 30, h: 22 }, // firma + nombre director
      { t: 57, l: 61, w: 34, h: 12 }, // Alumn@ + Francesc Riera Romea (derecha)
    ],
  },
];

function CornerMarks() {
  return (
    <div aria-hidden className="absolute inset-3 pointer-events-none">
      <span className="absolute top-0 left-0 w-3 h-px bg-[#C9A84C]" />
      <span className="absolute top-0 left-0 w-px h-3 bg-[#C9A84C]" />
      <span className="absolute top-0 right-0 w-3 h-px bg-[#C9A84C]" />
      <span className="absolute top-0 right-0 w-px h-3 bg-[#C9A84C]" />
      <span className="absolute bottom-0 left-0 w-3 h-px bg-[#C9A84C]" />
      <span className="absolute bottom-0 left-0 w-px h-3 bg-[#C9A84C]" />
      <span className="absolute bottom-0 right-0 w-3 h-px bg-[#C9A84C]" />
      <span className="absolute bottom-0 right-0 w-px h-3 bg-[#C9A84C]" />
    </div>
  );
}

function DiplomaModal({ cred, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[70] bg-[#06231D]/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="diploma-modal"
    >
      <motion.div
        initial={{ scale: 0.94, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94, y: 20 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full min-w-0 max-w-[1000px] max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between mb-4 text-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A84C]">
              {cred.institution}
            </div>
            <div className="font-display text-lg md:text-xl truncate">{cred.title}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="diploma-modal-close"
            className="w-11 h-11 rounded-full border border-white/25 grid place-items-center text-white hover:bg-[#C9A84C] hover:text-[#06231D] hover:border-[#C9A84C] transition-colors flex-shrink-0"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex-1 min-h-0 min-w-0 rounded-xl bg-white shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden flex items-center justify-center p-2">
          <div className="relative min-w-0 min-h-0" style={{ width: "fit-content", height: "fit-content", maxWidth: "100%", maxHeight: "100%" }}>
            <img
              src={cred.image}
              alt={`${cred.title} — ${cred.institution}`}
              className="block w-auto h-auto select-none"
              style={{ maxWidth: "100%", maxHeight: "calc(90vh - 160px)" }}
              draggable={false}
            />
            {cred.redactions?.map((r, idx) => (
              <span
                key={idx}
                aria-hidden
                className="absolute backdrop-blur-md bg-white/10 rounded-sm"
                style={{
                  top: `${r.t}%`,
                  left: `${r.l}%`,
                  width: `${r.w}%`,
                  height: `${r.h}%`,
                }}
              />
            ))}
          </div>
        </div>

        <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/50 text-center">
          Datos personales difuminados por privacidad · documento oficial disponible a petición
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function Credentials() {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="credenciales"
      data-testid="credentials-section"
      className="relative bg-[#F7F9F8] text-[#06231D] py-28 lg:py-40 grain scroll-mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-16 lg:mb-24">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="font-serif-italic text-[#085041] text-[9rem] lg:text-[12rem] leading-none block">
                07
              </span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#F7F9F8] bg-[#06231D] px-3 py-2 rounded-md">
                Credenciales
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[22ch] mx-auto mb-6">
              Formación <span className="font-serif-italic text-[#085041] font-normal">acreditada</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[#06231D]/70 text-lg leading-relaxed max-w-2xl mx-auto font-light text-center">
              Psicología, gestión del talento y coaching. La formación es real
              y verificable a petición.
            </p>
          </Reveal>
        </div>

        <div className="flex flex-col gap-6 lg:gap-8">
          {CREDENTIALS.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative rounded-2xl border border-[#C9A84C]/35 hover:border-[#C9A84C]/70 bg-[linear-gradient(180deg,#ffffff_0%,#F1F5F2_100%)] shadow-[0_30px_60px_-40px_rgba(6,35,29,0.35),0_6px_18px_-10px_rgba(6,35,29,0.08)] hover:-translate-y-1.5 hover:shadow-[0_50px_90px_-36px_rgba(6,35,29,0.5),0_24px_48px_-24px_rgba(201,168,76,0.3)] transition-[transform,box-shadow,border-color] duration-500 ease-out overflow-hidden"
              data-testid={`credential-${i}`}
            >
              <span aria-hidden className="absolute top-0 left-0 h-px w-32 brand-gradient" />
              <span aria-hidden className="absolute bottom-0 right-0 h-px w-32 brand-gradient" />
              <span aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-[80px] opacity-0 group-hover:opacity-[0.12] transition-opacity duration-700 pointer-events-none" style={{ background: "radial-gradient(circle,#C9A84C 0%, transparent 70%)" }} />
              <CornerMarks />

              <div className="p-8 lg:p-10 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-start">
                <div className="flex items-center gap-4">
                  <span
                    aria-hidden
                    className="w-11 h-11 rounded-full border border-[#C9A84C]/55 grid place-items-center text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-[#06231D] transition-colors"
                  >
                    <GraduationCap size={16} strokeWidth={1.6} />
                  </span>
                  <span className="font-serif-italic text-[#C9A84C] text-2xl md:text-3xl leading-none">
                    {c.n}
                  </span>
                </div>

                <div className="min-w-0 max-w-[62ch]">
                  <h3 className="font-display text-2xl md:text-[26px] lg:text-[28px] font-medium tracking-tight leading-[1.15] text-[#06231D]">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[12px] uppercase tracking-[0.16em] text-[#C9A84C] font-medium">
                    {c.institution}
                  </p>
                  <p className="mt-5 text-[#06231D]/70 text-[15px] leading-[1.7] font-light text-justify hyphens-auto">
                    {c.description}
                  </p>

                  <button
                    type="button"
                    onClick={() => setOpen(c)}
                    data-testid={`credential-view-${i}`}
                    className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium text-[#085041] hover:text-[#C9A84C] transition-colors link-underline"
                  >
                    <Eye size={13} strokeWidth={1.8} />
                    Ver título
                  </button>
                </div>

                <div className="justify-self-start md:justify-self-end pr-1 md:pt-1">
                  <span className="font-serif-italic text-[#085041] text-4xl md:text-5xl lg:text-[56px] leading-none">
                    {c.year}
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-[11px] uppercase tracking-[0.22em] text-[#06231D]/45">
            Documentación oficial completa disponible bajo solicitud.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && <DiplomaModal cred={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
}
